export type ScreeningPiiKind = 'mobile' | 'email' | 'address';

export const SCREENING_PII_ALERT = {
  title: "We can't ask for that",
  message:
    "Screening questions can't ask for a mobile number, email address, or home/delivery address. Those details are protected and already available from secure sources. Please ask a different qualifying question.",
  confirmText: 'Got it',
  cancelText: 'Cancel',
  type: 'alert' as const,
  icon: 'info' as const,
};

/** Campaign language that mentions a blocked term without asking for personal details. */
const ALLOWLIST: RegExp[] = [
  /\bemail marketing\b/g,
  /\bemail campaigns?\b/g,
  /\bnewsletters?\b/g,
  /\bmailing lists?\b/g,
  /\bcold emails?\b/g,
  /\baddress the (?:brief|audience|topic|camera|product|brand)\b/g,
  /\bform of address\b/g,
  /\bphone cases?\b/g,
  /\bphone reviews?\b/g,
  /\bunbox(?:ing)? (?:a )?phones?\b/g,
  /\bsmartphones? reviews?\b/g,
  /\bmobile (?:apps?|games?|applications?)\b/g,
];

const EMAIL_PATTERN =
  /\b(?:your |contact |work |personal )?emails?(?: (?:address|id|account))?\b/;

const MOBILE_PATTERN =
  /\b(?:(?:mobile|phone|cell|telephone) (?:number|no|num)|contact numbers?|whatsapp (?:number|no|num)|your whatsapp|your (?:phone|mobile|cell|telephone)|(?:provide|share|enter|give|leave|send|drop) (?:us |me )?(?:your )?(?:phone|mobile|cell|telephone|number)|(?:phone|mobile|cell|telephone) please|what(?: s| is) (?:your )?(?:phone|mobile|cell|number))\b/;

const ADDRESS_PATTERN =
  /\b(?:(?:delivery|shipping|home|postal|mailing|residential|billing) address|your address|(?:post ?code|postcode|zip ?code|postal code)|where (?:should|can|do|would) we (?:send|ship|deliver)|(?:provide|share|enter|give|leave|send) (?:us |me )?(?:your )?address|where(?:abouts)? (?:do|does|are|is) (?:you|the (?:applicant|creator)) (?:currently )?(?:live|living|reside|residing|stay|staying)|where (?:are|is) (?:you|the (?:applicant|creator)) based|what(?: s| is) your (?:place of )?residence|where is your home|which (?:city|town|area) do you live(?: in)?|what (?:city|town|area) do you live(?: in)?)\b/;

function normalizeQuestion(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/e\s*-\s*mail/g, 'email')
    .replace(/\be\s+mail\b/g, 'email')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function maskAllowlisted(text: string): string {
  let masked = text;
  for (const pattern of ALLOWLIST) {
    pattern.lastIndex = 0;
    masked = masked.replace(pattern, ' ');
  }
  return masked.replace(/\s+/g, ' ').trim();
}

export function detectScreeningPii(question: string): ScreeningPiiKind | null {
  const normalized = maskAllowlisted(normalizeQuestion(question));
  if (!normalized) {
    return null;
  }
  if (EMAIL_PATTERN.test(normalized)) {
    return 'email';
  }
  if (MOBILE_PATTERN.test(normalized)) {
    return 'mobile';
  }
  if (ADDRESS_PATTERN.test(normalized)) {
    return 'address';
  }
  return null;
}

export function withoutProtectedScreeningQuestions<T extends { question: string }>(questions: T[]): T[] {
  return questions.filter((question) => detectScreeningPii(question.question) === null);
}
